import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Lock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PathwayHero from '../components/PathwayHero';
import Stepper from '../components/Stepper';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { firstYearData } from '../data/pathwaysMock';

const FirstYearPathway = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <Navbar />
      {/* Hero */}
      <PathwayHero {...firstYearData.hero} />

      {/* Stepper */}
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <Stepper steps={firstYearData.stepper} currentStep={0} />
      </div>

      {/* Engineering Specializations */}
      <section className="py-16 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 text-center">
            Engineering Tracks
          </h2>
          <p className="text-gray-600 text-center mb-10">Choose your specialization → build real skills → land roles</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {firstYearData.specializationGrid.engineering.map((track, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                whileHover={{ y: -4 }}
                className="group p-6 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-xl transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                    {track.name}
                  </h3>
                  <Badge className={
                    track.aiRisk === 'Low' ? 'bg-green-100 text-green-700 border-green-200' :
                    track.aiRisk === 'Medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                    'bg-red-100 text-red-700 border-red-200'
                  }>
                    AI: {track.aiRisk}
                  </Badge>
                </div>
                
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-2">Leads to:</p>
                  <div className="flex flex-wrap gap-2">
                    {track.roles.map((role, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <Button asChild variant="outline" size="sm" className="w-full group-hover:bg-teal-50 group-hover:border-teal-300">
                  <Link to={track.link}>
                    View Track <ArrowRight size={14} className="ml-2" />
                  </Link>
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Management Specializations */}
      <section className="py-16 px-4 md:px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 text-center">
            Management Tracks
          </h2>
          <p className="text-gray-600 text-center mb-10">BBA/MBA specializations with real business skills</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {firstYearData.specializationGrid.management.map((track, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                whileHover={{ y: -4 }}
                className="group p-6 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-xl transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                    {track.name}
                  </h3>
                  <Badge className={
                    track.aiRisk === 'Low' ? 'bg-green-100 text-green-700 border-green-200' :
                    track.aiRisk === 'Medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                    'bg-red-100 text-red-700 border-red-200'
                  }>
                    AI: {track.aiRisk}
                  </Badge>
                </div>
                
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-2">Leads to:</p>
                  <div className="flex flex-wrap gap-2">
                    {track.roles.map((role, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <Button asChild variant="outline" size="sm" className="w-full group-hover:bg-teal-50 group-hover:border-teal-300">
                  <Link to={track.link}>
                    View Track <ArrowRight size={14} className="ml-2" />
                  </Link>
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap Preview */}
      <section className="py-16 px-4 md:px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
            8-12 Week Roadmap
          </h2>
          <div className="space-y-4">
            {firstYearData.roadmapPreview.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="flex items-center p-6 bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-xl"
              >
                <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                  {index + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{milestone.weeks}</h3>
                  <p className="text-sm text-gray-600">{milestone.focus}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Preview section removed */}

      {/* AI Risk Explainer */}
      <section className="py-12 px-4 md:px-6 bg-teal-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start space-x-4">
            <Zap className="text-teal-600 flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="font-semibold text-teal-900 mb-2">What is AI Risk?</h3>
              <p className="text-sm text-teal-800">{firstYearData.aiRiskExplainer}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4 md:px-6 bg-gradient-to-br from-teal-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-bold mb-6">
            Choose your specialization. Build real skills.
          </h2>
          <Button asChild size="lg" variant="secondary" className="text-lg">
            <Link to="/tracks">Browse All Tracks</Link>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default FirstYearPathway;
